import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowLeft,
  Loader2,
  Heart,
  Mail,
  User,
  Package,
} from "lucide-react";
import { trackPageView } from "@/lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARD_DECK_PRICE = 4000;

export default function WaitlistPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [checkoutDecks, setCheckoutDecks] = useState([]);
  const [totalPrice, setTotalPrice] = useState(CARD_DECK_PRICE);

  useEffect(() => {
    trackPageView("waitlist");

    // Load checkout decks from localStorage (new cart format with quantities)
    const decks = localStorage.getItem("kardiy_checkout_decks");
    if (decks) {
      const parsedDecks = JSON.parse(decks);
      setCheckoutDecks(parsedDecks);
      // Calculate total with quantities
      const total = parsedDecks.reduce(
        (sum, item) => sum + (item.quantity || 1) * CARD_DECK_PRICE,
        0,
      );
      setTotalPrice(total);
    }
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Kérlek, töltsd ki az összes mezőt");
      return;
    }

    if (!formData.email.includes("@")) {
      toast.error("Kérlek, adj meg egy érvényes email címet");
      return;
    }

    setLoading(true);

    try {
      // Get all project IDs and quantities for the checkout
      const items =
        checkoutDecks.length > 0
          ? checkoutDecks.map((d) => ({ id: d.id, quantity: d.quantity || 1 }))
          : [{ id: projectId, quantity: 1 }];

      await axios.post(`${API}/waitlist`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        project_ids: items.map((i) => i.id),
        total_price: totalPrice,
        items: items,
      });

      // Clear cart from localStorage
      localStorage.removeItem("kardiy_checkout_decks");
      localStorage.removeItem("kardiy_cart");
      localStorage.removeItem("kardiy_saved_decks");

      navigate("/thank-you");
    } catch (error) {
      toast.error("Hiba történt. Kérlek, próbáld újra.");
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Megrendelés | Kardiy</title>
        <meta
          name="description"
          content="Rendeld meg személyre szabott kérdéskártya-paklidat – közvetlenül a nyomdából."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-[#FDFBF7] to-[#FFF1F2]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-stone-100">
          <div className="max-w-md mx-auto px-4">
            <div className="flex items-center justify-between h-14">
              <button
                onClick={() => navigate(`/editor/${projectId}`)}
                className="flex items-center gap-2 text-stone-500 hover:text-[#292524] transition-colors p-2 -ml-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FF6B6B]" />
                <span className="font-heading text-lg font-bold text-[#292524]">
                  Kardiy
                </span>
              </div>
              <div className="w-10" />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-4 py-8">
          <div className="max-w-md mx-auto">
            {/* Heart Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[#FF52A9]/10 flex items-center justify-center">
                <Heart className="w-10 h-10 text-[#FF52A9]" fill="#FF52A9" />
              </div>
            </div>

            {/* Message */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#292524] mb-4 leading-tight">
                Köszönjük a bizalmad!
              </h1>
              <p className="text-stone-600 leading-relaxed">
                Nagy örömmel látjuk, hogy te is hiszel az egyedi
                beszélgetésindító kártyák erejében. A Kardiy béta verziója
                jelenleg az utolsó simításoknál tart, hogy a legmagasabb prémium
                minőséget nyújthassuk.
              </p>
              <p className="text-stone-600 leading-relaxed pt-4">
                Az, hogy végigkattintottad az oldalt, számunkra nagyon fontos
                visszajelzés – köszönjük
              </p>
            </div>

            {/* Discount Badge */}
            <div className="bg-[#FFA952]/20 rounded-2xl p-4 text-center mb-8">
              {/* <div className="bg-[#FFA952]/20 rounded-2xl p-4 text-center mb-8">
              {checkoutDecks.length > 0 && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Package className="w-5 h-5 text-[#FF5252]" />
                  <span className="text-sm font-medium text-[#292524]">
                    {checkoutDecks.reduce(
                      (sum, d) => sum + (d.quantity || 1),
                      0,
                    )}{" "}
                    pakli kiválasztva
                  </span>
                </div>
              )} */}
              <p className="text-[#292524] font-medium">
                Szeretnénk ezt meghálálni egy{" "}
                <span className="font-bold text-[#FF5252] mt-2">
                  50%-os kuponnal
                </span>{" "}
                , amit az első Kardiy kártyapaklidnál használhatsz fel, amikor
                elindul a rendszer.
              </p>
              {/* <p className="font-heading text-2xl font-bold text-[#FF5252] mt-2">
                50% kedvezmény
              </p> */}
              {/* <p className="text-sm text-stone-500 mt-1">
                az első egyedi paklidra
              </p> */}

              {checkoutDecks.length > 0 && (
                <div className="flex items-center justify-center gap-2 mb-2 pt-4">
                  <Package className="w-5 h-5 text-[#FF5252]" />
                  <span className="text-sm font-medium text-[#292524] ">
                    {checkoutDecks.reduce(
                      (sum, d) => sum + (d.quantity || 1),
                      0,
                    )}{" "}
                    pakli kiválasztva
                  </span>
                </div>
              )}
              <p className="text-lg font-bold text-[#292524] mt-3">
                Végösszeg: {totalPrice.toLocaleString()} Ft
              </p>
            </div>

            {/* Form */}
            <Card className="bg-white rounded-3xl shadow-lg border-0">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="lastName"
                        className="text-sm font-medium text-[#292524]"
                      >
                        Vezetéknév
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Kiss"
                          className="pl-10 rounded-xl border-stone-200 h-12"
                          data-testid="lastname-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="firstName"
                        className="text-sm font-medium text-[#292524]"
                      >
                        Keresztnév
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Anna"
                          className="pl-10 rounded-xl border-stone-200 h-12"
                          data-testid="firstname-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-sm font-medium text-[#292524]"
                    >
                      Email cím
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="pelda@email.com"
                        className="pl-10 rounded-xl border-stone-200 h-12"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-btn"
                    className="w-full bg-[#FF5252] hover:bg-[#FF6B6B] text-white font-bold py-6 rounded-full active:scale-95 transition-all text-lg mt-6"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Küldés...
                      </>
                    ) : (
                      "Kérem a kupont!"
                    )}
                  </Button>
                </form>

                <p className="text-xs text-stone-400 text-center mt-4">
                  Adataidat biztonságban kezeljük és nem adjuk ki harmadik
                  félnek.
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </>
  );
}
